import { Controller } from "@hotwired/stimulus";
import ApexCharts from "apexcharts";

// Renders a line chart for a body metric's history.
// Pass dates and values as JSON arrays via data values.
export default class extends Controller {
  static values = {
    dates: Array,
    values: Array,
    label: { type: String, default: "" },
    unit: { type: String, default: "" },
    color: { type: String, default: "#3b82f6" },
  };

  connect() {
    this.renderChart();
    this.observer = new MutationObserver(() => this.renderChart());
    this.observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  disconnect() {
    this.observer?.disconnect();
    this.chart?.destroy();
  }

  renderChart() {
    this.chart?.destroy();

    const dark = document.documentElement.classList.contains("dark");
    const textColor = dark ? "#9ca3af" : "#6b7280";
    const gridColor = dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)";
    const bg = dark ? "#1f2937" : "#ffffff";
    const unit = this.unitValue;

    this.chart = new ApexCharts(this.element, {
      chart: {
        type: "area",
        height: 200,
        sparkline: { enabled: false },
        toolbar: { show: false },
        background: "transparent",
        fontFamily: "inherit",
        animations: { enabled: true, easing: "easeinout", speed: 400 },
      },
      series: [
        {
          name: this.labelValue,
          data: this.valuesValue.map((v) => parseFloat(v)),
        },
      ],
      xaxis: {
        categories: this.datesValue,
        labels: {
          style: { colors: textColor, fontSize: "10px" },
          formatter: (val) => {
            if (!val) return "";
            const d = new Date(val + "T00:00:00");
            return d.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            });
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          style: { colors: textColor, fontSize: "10px" },
          formatter: (val) => `${val}${unit}`,
        },
      },
      grid: {
        borderColor: gridColor,
        strokeDashArray: 3,
        padding: { left: 8, right: 8, top: 0, bottom: 0 },
      },
      stroke: { curve: "smooth", width: 2.5 },
      colors: [this.colorValue],
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.3,
          opacityTo: 0.05,
          stops: [0, 100],
        },
      },
      dataLabels: { enabled: false },
      tooltip: {
        theme: dark ? "dark" : "light",
        y: { formatter: (val) => `${val} ${unit}` },
      },
    });

    this.chart.render();
  }
}
